import { freeze, produce } from 'immer';
import { AbstractBaseListStore } from './abstractBaseListStore';
import { EEntityType } from '@/editor/types';
import { TGlobalStores } from '..';
import { getIncrementId } from '@/editor/utils';
import { EPatchType, IPatchItem } from '@/editor/types/baseStore';
import { AbstractHistoryStore } from '../../history/abstractHistoryStore';
import { cloneDeep } from 'lodash-es';

export class BaseListStore<T extends { id: string }> implements AbstractBaseListStore<T>, AbstractHistoryStore {
  public id: number;
  public dataType: EEntityType | null;
  private _dataMap: Map<string, T>;
  private _list: string[] | null;
  // 数据实际变更后的通知回调。undo/redo 通过 applyChange 直接调用本类的 add/update/remove/clear/addAll，
  // 不经过外层 store 的封装方法，外层如需感知数据变更（如缓存失效、补齐附属数据）必须依赖此回调。
  // changedId 为本次变更的数据 id；clear / addAll 是整表替换，不带 id，回调方需按全量处理
  private _onDataChanged?: (changedId?: string) => void;

  private _history;

  constructor(
    gs: TGlobalStores,
    options: { keepOrder?: boolean; dataType?: EEntityType; onDataChanged?: (changedId?: string) => void } = {}
  ) {
    const { keepOrder = false, dataType, onDataChanged } = options;

    this._dataMap = new Map();
    this._list = keepOrder ? [] : null;
    this.dataType = dataType || null;
    this._onDataChanged = onDataChanged;

    this._history = gs.history;
    this.id = getIncrementId();
    this._history.addStore(this);
  }

  add(data: T, order?: number) {
    if (!data.id || this._dataMap.has(data.id)) return;

    // 创建不可变副本，确保存储的值是不可变的
    this._dataMap.set(data.id, freeze(data));

    if (this._list) {
      if (typeof order === 'number') {
        this._list.splice(order, 0, data.id);
      } else {
        this._list.push(data.id);
      }
    }

    // 记录反转操作
    this.recordChange({ sId: this.id, type: EPatchType.Remove, id: data.id });

    this._onDataChanged?.(data.id);
  }

  update(id: string, data: DeepPartial<T>) {
    if (!id || !data) return;

    const oldData = this._dataMap.get(id);
    if (!oldData) {
      console.error('update data not found', id, this.dataType);
      return;
    }

    // 传入的 data 参数不可变，不 clone 的话后面 deleteProperty 会修改原对象，导致调用处的对象被修改
    const cloneData = cloneDeep(data);

    const newData = produce(oldData, (draft) => {
      Reflect.deleteProperty(cloneData, 'id');
      Object.assign(draft, cloneData);
    });
    this._dataMap.set(id, newData);

    // 记录反转操作
    const invertData: Partial<T> = {};
    Object.keys(data).forEach((key) => {
      invertData[key as keyof T] = oldData[key as keyof T];
    });
    this.recordChange({
      sId: this.id,
      type: EPatchType.Update,
      id,
      data: invertData
    });

    this._onDataChanged?.(id);
  }

  frequentlyUpdate(id: string, data: Partial<T>) {
    if (!id || !data) return;
    this.update(id, data);
    this._history.mergeUpdateChanges();
  }

  remove(id: string) {
    if (!id) return;
    const data = this._dataMap.get(id);
    if (!data) {
      console.error('remove data not found', id, this.dataType);
      return;
    }

    const order = this._list?.indexOf(id);

    this._dataMap.delete(id);

    if (this._list && typeof order === 'number') {
      this._list.splice(order, 1);
    }

    // 记录反转操作
    const invertData: IPatchItem<T> = { sId: this.id, type: EPatchType.Add, id, data };
    if (typeof order === 'number') invertData.order = order;
    this.recordChange(invertData);

    this._onDataChanged?.(id);
  }

  // remove all
  clear() {
    if (this._dataMap.size === 0) return;

    let dataList;
    if (this._list) {
      dataList = this._list.map((id) => this._dataMap.get(id)).filter((data) => data !== undefined);
    } else {
      dataList = Array.from(this._dataMap.values());
    }

    this.recordChange({ sId: this.id, type: EPatchType.AddAll, list: dataList });

    this._dataMap.clear();
    if (this._list) this._list.length = 0;

    this._onDataChanged?.();
  }

  addAll(list: T[]) {
    list.forEach((data) => {
      this._dataMap.set(data.id, freeze(data));

      if (this._list) {
        this._list.push(data.id);
      }
    });

    this.recordChange({ sId: this.id, type: EPatchType.Clear });

    this._onDataChanged?.();
  }

  get(id: string) {
    return this._dataMap.get(id);
  }

  getList() {
    if (!this._list) return Array.from(this._dataMap.values());
    return this._list.map((id) => this._dataMap.get(id)).filter((data) => data !== undefined);
  }

  recordChange(change: IPatchItem<T>) {
    this._history.recordDataChange(change);
  }

  applyChange(change: IPatchItem<T>) {
    switch (change.type) {
      case EPatchType.Add:
        this.add(change.data, change.order);
        break;
      case EPatchType.Remove:
        {
          this.remove(change.id);
        }
        break;
      case EPatchType.Update:
        this.update(change.id, change.data);
        break;
      case EPatchType.Clear:
        this.clear();
        break;
      case EPatchType.AddAll:
        this.addAll(change.list);
        break;
    }
  }
}
